<template>
  <div v-if="show" :class="$style.container" @click="handleClicked">
    <div v-if="showImage" :class="$style['image-container']">
      <img :src="imageSrc" :alt="altText" />
    </div>
    <div :class="$style['info-container']">
      <div :class="$style.title">
        <span>{{ title }}</span>
      </div>
      <div :class="$style.description">{{ description }}</div>
    </div>
    <div :class="$style['icon-container']">
      <NsIcon
        :class="$style['ns-icon']"
        icon-name="icon-chevron-right"
        size="icon-lg"
      />
    </div>
  </div>
</template>

<script>
import "@nuskin/ns-core-styles/src/dist/main.css";

import { NsIcon } from "@nuskin/design-components";

export default {
  name: "NsInformationButton",
  components: {
    NsIcon
  },
  props: {
    imageSrc: {
      type: String,
      default: ""
    },
    imageAlt: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    description: {
      type: String,
      default: ""
    },
    url: {
      type: String,
      default: ""
    },
    opensNewTab: Boolean
  },
  computed: {
    show() {
      return this.showImage || !!this.title || !!this.description;
    },
    showImage() {
      return !!this.imageSrc && !this.isMobile;
    },
    altText() {
      return this.imageAlt;
    },
    isMobile() {
      return this.$mq === "mobile" || this.$mq === "phablet";
    }
  },
  methods: {
    handleClicked() {
      window.open(this.url, this.opensNewTab ? "_blank" : "_top");
    }
  }
};
</script>

<style module lang="scss">
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

$borderColor: #e1e1e1;
$titleLineHeight: 26px;
$descriptionLineHeight: 22px;

.container {
  position: relative;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  padding: 40px 30px 40px 39px;
  color: #332f20;
  background-color: white;
  border: 1px solid $borderColor;
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    padding: 39px 29px 39px 38px;
    border-width: 2px;
    border-color: darken($borderColor, 15%);
  }
}

.image-container,
.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-container {
  margin-right: 35px;

  img {
    width: 66px;
    height: 66px;
    object-fit: contain;
  }
}

.info-container {
  flex: 1;
  display: flex;
  flex-direction: column;

  .title {
    font-style: italic;
    font-weight: bold;
    font-size: 20px;
    line-height: $titleLineHeight;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  .description {
    margin-top: 5px;
    font-style: normal;
    font-weight: 300;
    font-size: 16px;
    line-height: $descriptionLineHeight;
    color: #444444;
  }
}

.icon-container {
  margin-left: 5px;
}

@media (max-width: 767px) {
  .container {
    padding: 22px 14px 19px 26px;

    .image-container {
      display: none;
    }

    &:hover {
      padding: 21px 13px 18px 25px;
    }
  }
}

@media (min-width: 768px) {
  .info-container {
    .title {
      display: flex;
      flex-direction: column-reverse;
      height: $titleLineHeight * 3;

      span {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        -webkit-line-clamp: 3;
      }
    }

    .description {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: 5;
      height: $descriptionLineHeight * 5;
    }
  }
}

@media (min-width: 1024px) {
  .info-container {
    .title {
      height: $titleLineHeight * 2;

      span {
        -webkit-line-clamp: 2;
      }
    }

    .description {
      -webkit-line-clamp: 4;
      height: $descriptionLineHeight * 4;
    }
  }
}
</style>
